"use client"

import { useState, useEffect } from "react" // Ensure useEffect is imported
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table"
import { Badge } from "../../ui/badge"
import { Eye, Info, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { Pagination } from "../../common/pagination-dynamic"
import { LeaveReviewModalHR } from "../../modals/leave-review-modal-hr"
import { useToast } from "../../hooks/use-toast"
import axios from "axios"

interface LeaveDayDetail {
  leaveDate: string
  dayType: string
}

interface LeaveRequest {
  id: number
  requestBy: string
  leaveType: string
  fromDate: string
  toDate: string
  totalDays: number
  appliedOn: string
  reason: string
  status: string
  numericStatus?: number
  hrApprovedBy?: string
  hrApprovedOn?: string
  hrComment?: string
  leaveDayDetails?: LeaveDayDetail[]
}


export function LeaveRequestApprovalsPage() {
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState<LeaveRequest | null>(null)
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [pendingRequests, setPendingRequests] = useState<LeaveRequest[]>([])
  const [sortField, setSortField] = useState<"requestBy" | "leaveType" | "fromDate" | "toDate" | "appliedOn" | null>(null)
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const { toast } = useToast()

  // Format ISO date strings like 2025-10-16 to 16-Oct-2025
  const formatDate = (value?: string) => {
    if (!value) return ""
    try {
      const d = new Date(value)
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/\s/g, "-")
    } catch {
      return value
    }
  }

  const fetchApprovals = async () => {
    try {
      const resp = await axios.post(
        process.env.REACT_APP_API_URL + "/api/Leave/hr-approvals",
        {},
        { withCredentials: true }
      )
      // Handle both direct array and nested data structure
      const list = Array.isArray(resp?.data?.data) ? resp.data.data : (Array.isArray(resp?.data) ? resp.data : [])
      
      // Debug: Log first item to see structure
      if (list.length > 0) {
        ////console.log("First HR approval item:", list[0]);
        ////console.log("First item leaveDayDetails:", list[0]?.leaveDayDetails);
      }
      
      const mapped: LeaveRequest[] = list
        .filter((item: any) => {
          const status = typeof item.status === "number" ? item.status : undefined
          return status === 6 || status === 8
        })
        .map((item: any, index: number) => {
          // Ensure leaveDayDetails is properly handled
          const dayDetails = item.leaveDayDetails || item.leaveDayDetail || [];
          if (dayDetails && !Array.isArray(dayDetails)) {
            console.warn("leaveDayDetails is not an array:", dayDetails);
          }
          
          return {
            id: item.requestID ?? index + 1,
            requestBy: item.requestedBy ?? "",
            leaveType: item.leaveName ?? "",
            fromDate: formatDate(item.leaveStartDate),
            toDate: formatDate(item.leaveEndDate),
            totalDays: item.totalDays ?? 0,
            appliedOn: formatDate(item.requestDate),
            reason: item.reason ?? "",
            status: item.status === 6 ? "pending_hr_approval" : "rejected_by_hr",
            numericStatus: typeof item.status === "number" ? item.status : undefined,
            hrApprovedBy: item.hrName ?? undefined,
            hrApprovedOn: item.hrResponseOn ?? undefined,
            hrComment: item.hrComment ?? undefined,
            leaveDayDetails: Array.isArray(dayDetails) ? dayDetails : [],
          ...(item.managerName ? { managerName: item.managerName } : {}),
          ...(item.approvedByName ? { approvedByName: item.approvedByName } : {}),
          ...(item.modifiedByName ? { modifiedByName: item.modifiedByName } : {}),
          ...(item.modUser ? { modUser: item.modUser } : {}),
          ...(item.managerResponseOn ? { managerResponseOn: item.managerResponseOn } : { managerResponseOn: null }),
          ...(item.modifiedOn ? { modifiedOn: item.modifiedOn } : {}),
          ...(item.comment ? { comments: item.comment } : {}),
          ...(item.managerResponse ? { managerResponse: item.managerResponse } : {}),
          };
        })

      const sorted = [...mapped].sort((a, b) => {
        if (a.numericStatus === 6 && b.numericStatus === 8) return -1
        if (a.numericStatus === 8 && b.numericStatus === 6) return 1
        const ad = a.appliedOn ? Date.parse(a.appliedOn.replace(/-/g, ' ')) : 0
        const bd = b.appliedOn ? Date.parse(b.appliedOn.replace(/-/g, ' ')) : 0
        return bd - ad
      })
      setPendingRequests(sorted)
    } catch (err: any) {
      toast({
        title: "Failed to load approvals",
        description: err?.message || "Unexpected error",
        duration: 4000,
      })
    }
  }

  useEffect(() => {
    fetchApprovals()
  }, [])

  const handleReview = (request: LeaveRequest) => {
    setSelectedLeaveRequest(request)
    setIsReviewModalOpen(true)
  }

  const handleApprove = async (comment: string) => {
    if (selectedLeaveRequest) {
      
        toast({
          title: "Leave Approved",
          description: `Leave request for ${selectedLeaveRequest.requestBy} has been approved.`,
          duration: 3000,
          className: "border-2 border-green-500",
        })
      
    }
    setIsReviewModalOpen(false)
    setSelectedLeaveRequest(null)
    fetchApprovals()
  }

  const handleReject = async (comment: string) => {
    if (selectedLeaveRequest) {
      
        toast({
          title: "Leave Rejected",
          description: `Leave request for ${selectedLeaveRequest.requestBy} has been rejected.`,
          duration: 3000,
          className: "border-2 border-red-500",
        })
      
    }
    setIsReviewModalOpen(false)
    setSelectedLeaveRequest(null)
    fetchApprovals()
  }

  // Sorting function
  const handleSort = (field: "requestBy" | "leaveType" | "fromDate" | "toDate" | "appliedOn") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
    setCurrentPage(1) // Reset to first page when sorting
  }

  // Get sort icon
  const getSortIcon = (field: "requestBy" | "leaveType" | "fromDate" | "toDate" | "appliedOn") => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4" />
    return sortDirection === "asc" ? 
      <ArrowUp className="h-4 w-4" /> : 
      <ArrowDown className="h-4 w-4" />
  }

  // Apply sorting to data
  let sortedData = [...pendingRequests]
  if (sortField) {
    sortedData = [...pendingRequests].sort((a, b) => {
      let aValue: any, bValue: any
      
      switch (sortField) {
        case "requestBy":
          aValue = a.requestBy?.toLowerCase() || ""
          bValue = b.requestBy?.toLowerCase() || ""
          break
        case "leaveType":
          aValue = a.leaveType?.toLowerCase() || ""
          bValue = b.leaveType?.toLowerCase() || ""
          break
        case "fromDate":
        case "toDate":
          // Parse date strings for proper sorting (format: "DD-MMM-YYYY")
          const parseLeaveDate = (dateStr: string) => {
            if (!dateStr) return 0
            try {
              const [day, month, year] = dateStr.split("-")
              const monthMap: { [key: string]: string } = {
                "Jan": "01", "Feb": "02", "Mar": "03", "Apr": "04",
                "May": "05", "Jun": "06", "Jul": "07", "Aug": "08",
                "Sep": "09", "Oct": "10", "Nov": "11", "Dec": "12"
              }
              const monthNum = monthMap[month] || "01"
              const isoDate = `${year}-${monthNum}-${day.padStart(2, "0")}`
              return new Date(isoDate).getTime()
            } catch {
              return 0
            }
          }
          aValue = parseLeaveDate(sortField === "fromDate" ? (a.fromDate || "") : (a.toDate || ""))
          bValue = parseLeaveDate(sortField === "fromDate" ? (b.fromDate || "") : (b.toDate || ""))
          break
        case "appliedOn":
          // Parse date strings for proper sorting (format: "DD-MMM-YYYY")
          const parseAppliedDate = (dateStr: string) => {
            if (!dateStr) return 0
            try {
              const [day, month, year] = dateStr.split("-")
              const monthMap: { [key: string]: string } = {
                "Jan": "01", "Feb": "02", "Mar": "03", "Apr": "04",
                "May": "05", "Jun": "06", "Jul": "07", "Aug": "08",
                "Sep": "09", "Oct": "10", "Nov": "11", "Dec": "12"
              }
              const monthNum = monthMap[month] || "01"
              const isoDate = `${year}-${monthNum}-${day.padStart(2, "0")}`
              return new Date(isoDate).getTime()
            } catch {
              return 0
            }
          }
          aValue = parseAppliedDate(a.appliedOn || "")
          bValue = parseAppliedDate(b.appliedOn || "")
          break
        default:
          return 0
      }
      
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue
      }
      
      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })
  }

  const totalPages = Math.ceil(sortedData.length / (pageSize))
  const startIndex = (currentPage - 1) * (pageSize)
  const endIndex = startIndex + (pageSize)
  const currentData = sortedData.slice(startIndex, endIndex)

  return (
    <div className="space-y-6">
      <CardHeader className="p-0">
        <CardTitle className="text-xl font-semibold text-gray-900">
          Leave Request Approvals
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {pendingRequests.length === 0 ? (
          <Card className="border border-gray-200 rounded-lg shadow-sm bg-white">
            <CardContent className="p-8 text-center text-gray-500">
              <Info className="h-12 w-12 mx-auto mb-4 text-blue-500" />
              <h3 className="text-xl font-medium mb-2 text-gray-900">No Pending Leave Requests</h3>
              <p>You don't have any leave requests pending for approval.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 border-b border-gray-200">
                  <TableHead className="w-12 font-semibold text-gray-700 p-4">Sr. No.</TableHead>
                  <TableHead className="font-semibold text-gray-700 p-4">
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                      onClick={() => handleSort("requestBy")}
                    >
                      Request By
                      {getSortIcon("requestBy")}
                    </Button>
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700 p-4">
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                      onClick={() => handleSort("leaveType")}
                    >
                      Type of Leave
                      {getSortIcon("leaveType")}
                    </Button>
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700 p-4">
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                      onClick={() => handleSort("fromDate")}
                    >
                      From Date
                      {getSortIcon("fromDate")}
                    </Button>
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700 p-4">
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                      onClick={() => handleSort("toDate")}
                    >
                      To Date
                      {getSortIcon("toDate")}
                    </Button>
                  </TableHead>
                  <TableHead className="font-semibold text-gray-700 p-4">Total Days</TableHead>
                  <TableHead className="font-semibold text-gray-700 p-4">
                    <Button
                      variant="ghost"
                      className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                      onClick={() => handleSort("appliedOn")}
                    >
                      Applied On
                      {getSortIcon("appliedOn")}
                    </Button>
                  </TableHead>
                  <TableHead className="w-32 font-semibold text-gray-700 p-4">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentData.map((request, idx) => (
                  <TableRow key={request.id ?? `${startIndex + idx + 1}`} className="hover:bg-gray-50 border-b border-gray-100">
                   <TableCell className="p-4 text-center">
                      <div
                        className={`mx-auto w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 ${
                          request.numericStatus === 8 || request.status === "rejected_by_hr"
                            ? "border-red-300 bg-red-100 text-black"
                            : "border-transparent text-black bg-transparent"
                        }`}
                      >
                         {startIndex + idx + 1}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium text-gray-900 p-4">{request.requestBy}</TableCell>
                    <TableCell className="text-gray-700 p-4">{request.leaveType}</TableCell>
                    <TableCell className="text-gray-700 p-4">{request.fromDate}</TableCell>
                    <TableCell className="text-gray-700 p-4">{request.toDate}</TableCell>
                    <TableCell className="text-gray-700 p-4">{request.totalDays}</TableCell>
                    <TableCell className="text-gray-700 p-4">{request.appliedOn}</TableCell>
                    <TableCell className="p-4">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleReview(request)}
                          className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1 font-medium"
                        >
                          <Eye className="h-4 w-4" />
                          Review
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={sortedData.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
            />
          </>
        )}
      </CardContent>

      <LeaveReviewModalHR
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        leaveRequest={selectedLeaveRequest}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  )
}
